import { imagePathResolver } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { Link } from '../../Link/Link';
import { RichContent } from '../../RichContent/RichContent';
import type { MagazineArticle } from '../helpers/magazineCategory';
import { useMagazinePublishedDate } from '../hooks/useMagazinePublishedDate';
import { MagazinePromotedArticleAuthorAvatar } from './MagazinePromotedArticleAuthorAvatar';

type MagazinePromotedArticleContentProps = {
  article: MagazineArticle;
  baseImgUrl: string;
  'data-testid'?: string;
};

function MagazinePromotedArticleContent({ article, baseImgUrl, 'data-testid': dataTestId = 'magazine-promoted-carousel-content' }: MagazinePromotedArticleContentProps) {
  const t = useTranslations('magazine.promoted_carousel');
  const href = article.seoInfo.canonicalUrl;
  const formattedDate = useMagazinePublishedDate(article.publishedDate);
  const authorImageSrc = article.author.profilePicture?.filePath ? imagePathResolver(article.author.profilePicture.filePath, baseImgUrl) : undefined;

  return (
    <div className="flex flex-col gap-2" data-testid={dataTestId}>
      <div className="flex min-w-0 items-center gap-2 paragraph-s text-foreground" data-testid={`${dataTestId}-metadata`}>
        {article.category ? (
          <>
            <span className="truncate" data-testid={`${dataTestId}-category`}>
              {article.category.title}
            </span>
            <span className="size-[3px] shrink-0 rounded-full bg-foreground" aria-hidden="true" />
          </>
        ) : null}
        <time className="truncate" dateTime={article.publishedDate} data-testid={`${dataTestId}-date`}>
          {formattedDate}
        </time>
      </div>

      <h2 className="heading-4 lg:heading-3 text-foreground min-w-0 break-words">
        <Link href={href} simplified className="inline break-words whitespace-normal hover:underline" data-testid={`${dataTestId}-title-link`}>
          {article.title}
        </Link>
      </h2>

      {article.summary ? (
        <RichContent
          information={article.summary}
          tag="div"
          className="mt-2 min-w-0 break-words text-foreground [&_p]:!paragraph lg:[&_p]:!paragraph-l"
          data-testid={`${dataTestId}-excerpt`}
        />
      ) : null}

      <Link href={href} simplified className="text-foreground link-label w-fit underline" data-testid={`${dataTestId}-read-more`} data-track-name="Read More">
        {t('read_more')}
      </Link>

      <div className="mt-4 flex items-center gap-4" data-track-name="Author" data-track-provider={article.author.name}>
        <MagazinePromotedArticleAuthorAvatar name={article.author.name} imageSrc={authorImageSrc} />
        <address className="not-italic paragraph text-foreground" data-testid={`${dataTestId}-author`}>
          {t.rich('by', {
            author: article.author.name,
            link: chunks => <span className="paragraph-strong">{chunks}</span>,
          })}
        </address>
      </div>
    </div>
  );
}

export { MagazinePromotedArticleContent, type MagazinePromotedArticleContentProps };
